import {useQuery} from '@tanstack/react-query';
import {getMyUser, getSessions, IUpdateUserPreferencesRequest, IUser, updateMyPreferences, VerdocsEndpoint} from '@verdocs/js-sdk';
import {queryClient} from './queryClient';

/** The caller's own user record. Carries the sign-in details the Security screen needs: whether a
 * password is set, when it last changed, which providers are linked, and the MFA summary. */
export const useMyUser = () =>
  useQuery({
    queryKey: ['user', 'me'],
    queryFn: () => getMyUser(VerdocsEndpoint.getDefault()),
  });

/** Active login sessions, newest first, with the caller's own marked `current`. */
export const useSessions = () =>
  useQuery({
    queryKey: ['user', 'sessions'],
    queryFn: () => getSessions(VerdocsEndpoint.getDefault()),
  });

/** Saves preferences and writes the result into the cached user record, so every screen reading it updates at once. */
export const saveMyPreferences = async (changes: IUpdateUserPreferencesRequest) => {
  const preferences = await updateMyPreferences(VerdocsEndpoint.getDefault(), changes);
  queryClient.setQueryData<IUser>(['user', 'me'], (user) => (user ? {...user, preferences} : user));
  return preferences;
};

/** The Builder's contrast setting, from the user's preference. It stays `auto` (following the browser) until they choose. */
export const useContrast = (): 'auto' | 'normal' | 'high' => {
  const highContrast = useMyUser().data?.preferences?.high_contrast;
  if (typeof highContrast !== 'boolean') {
    return 'auto';
  }

  return highContrast ? 'high' : 'normal';
};
